import { useState, useRef, useCallback } from "react";

function calcEMI(principal, annualRate, years) {
  if (!principal || !annualRate || !years) return 0;
  const r = annualRate / 12 / 100;
  const n = years * 12;
  return Math.round(
    (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
  );
}

function fmtINR(val) {
  return "₹" + Number(Math.round(val)).toLocaleString("en-IN");
}

function RangeSlider({ value, min, max, step, onChange }) {
  const trackRef = useRef(null);
  const dragging = useRef(false);
  const [active, setActive] = useState(false);
  const pct = ((value - min) / (max - min)) * 100;

  const getVal = useCallback(
    (clientX) => {
      const rect = trackRef.current.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const raw = min + ratio * (max - min);
      return Math.min(max, Math.max(min, Math.round(raw / step) * step));
    },
    [min, max, step]
  );

  const startDrag = useCallback(
    (clientX) => {
      dragging.current = true;
      setActive(true);
      onChange(getVal(clientX));
      const move = (e) => {
        if (dragging.current)
          onChange(getVal(e.clientX ?? e.touches?.[0]?.clientX));
      };
      const end = () => {
        dragging.current = false;
        setActive(false);
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", end);
        window.removeEventListener("touchmove", move);
        window.removeEventListener("touchend", end);
      };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", end);
      window.addEventListener("touchmove", move, { passive: true });
      window.addEventListener("touchend", end);
    },
    [getVal, onChange]
  );

  return (
    <div
      className="rs-track"
      ref={trackRef}
      onMouseDown={(e) => { e.preventDefault(); startDrag(e.clientX); }}
      onTouchStart={(e) => startDrag(e.touches[0].clientX)}
    >
      <div className="rs-bg" />
      <div className="rs-fill" style={{ width: `${pct}%` }}>
        <div className={`rs-thumb${active ? " rs-thumb--active" : ""}`} />
      </div>
    </div>
  );
}

const EmiIcon = () => (
  <svg width="32" height="32" viewBox="0 0 48 56" fill="none">
    <rect x="4" y="2" width="40" height="52" rx="4" stroke="white" strokeWidth="2.2" fill="none"/>
    <rect x="10" y="8" width="28" height="12" rx="2" fill="white" opacity="0.9"/>
    <rect x="10" y="26" width="7" height="6" rx="1.2" fill="white"/>
    <rect x="20.5" y="26" width="7" height="6" rx="1.2" fill="white"/>
    <rect x="31" y="26" width="7" height="6" rx="1.2" fill="white"/>
    <rect x="10" y="36" width="7" height="6" rx="1.2" fill="white"/>
    <rect x="20.5" y="36" width="7" height="6" rx="1.2" fill="white"/>
    <rect x="31" y="36" width="7" height="6" rx="1.2" fill="white"/>
    <rect x="10" y="46" width="18" height="6" rx="1.2" fill="white"/>
    <rect x="31" y="46" width="7" height="6" rx="1.2" fill="white"/>
  </svg>
);

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;900&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .emi-section {
    background: #f0f0f0;
    padding: 48px 16px 56px;
    font-family: 'Roboto', Arial, sans-serif;
  }

  .emi-heading {
    text-align: center;
    font-size: clamp(1.3rem, 4vw, 1.9rem);
    font-weight: 700;
    color: #CF2230;
    margin-bottom: 28px;
  }

  .emi-wrap {
    max-width: 1200px;
    margin: 0 auto;
  }

  /* ── Card ── */
  .emi-card {
    background: #fff;
    border: 1px solid #d4d4d4;
    display: flex;
    flex-direction: row;
    align-items: stretch;
  }

  /* ── Tab strip (left on desktop, top on mobile) ── */
  .emi-tabs {
    width: 68px;
    flex-shrink: 0;
    border-right: 1px solid #d4d4d4;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background: #CF2230;
  }

  .emi-tab {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px 0;
    cursor: pointer;
    background: #CF2230;
  }

  /* ── Center pane ── */
  .emi-center {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  /* ── Sliders section ── */
  .emi-sliders {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    column-gap: 24px;
    padding: 24px 28px 20px;
    border-bottom: 1px solid #e8e8e8;
  }

  /* ── Details section ── */
  .emi-details {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    column-gap: 24px;
    padding: 18px 28px 22px;
  }

  /* ── Right price column ── */
  .emi-price-col {
    width: 190px;
    flex-shrink: 0;
    border-left: 1px solid #d4d4d4;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    text-align: center;
    gap: 10px;
  }

  /* ── Slider labels / values ── */
  .sl-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 500;
    color: #9a9a9a;
    letter-spacing: 0.9px;
    text-transform: uppercase;
    margin-bottom: 2px;
  }

  .sl-val {
    display: block;
    font-size: 1.1rem;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 12px;
  }

  .sl-hints {
    display: flex;
    justify-content: space-between;
    font-size: 0.63rem;
    color: #b0b0b0;
    margin-top: 5px;
  }

  /* ── Custom range slider ── */
  .rs-track {
    position: relative;
    height: 22px;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    touch-action: none;
  }

  .rs-bg {
    position: absolute;
    left: 0; right: 0;
    height: 3px;
    background: #d0d0d0;
    border-radius: 2px;
  }

  .rs-fill {
    position: absolute;
    left: 0;
    height: 3px;
    background: #CF2230;
    border-radius: 2px;
    display: flex;
    align-items: center;
  }

  .rs-thumb {
    position: absolute;
    right: -10px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #CF2230;
    box-shadow: 0 1px 5px rgba(207,34,48,0.4);
    transition: transform 0.1s, box-shadow 0.1s;
    cursor: grab;
    flex-shrink: 0;
  }

  .rs-thumb--active {
    cursor: grabbing;
    transform: translateY(-50%) scale(1.2);
    box-shadow: 0 2px 10px rgba(207,34,48,0.5);
  }

  /* ── Detail labels / values ── */
  .dl-label {
    display: block;
    font-size: 0.65rem;
    font-weight: 500;
    color: #9a9a9a;
    letter-spacing: 0.9px;
    text-transform: uppercase;
    margin-bottom: 6px;
  }

  .dl-val {
    font-size: clamp(1.1rem, 3vw, 1.55rem);
    font-weight: 900;
    color: #111;
    letter-spacing: -0.5px;
  }

  /* ── Price col labels ── */
  .price-label {
    font-size: 0.65rem;
    font-weight: 700;
    color: #1a1a1a;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  .price-divider {
    width: 40px;
    height: 2px;
    background: #e0e0e0;
  }

  .price-val {
    font-size: clamp(1.1rem, 2.5vw, 1.6rem);
    font-weight: 900;
    color: #111;
    white-space: nowrap;
    letter-spacing: -0.5px;
  }

  /* ════════════════════════════════
     RESPONSIVE BREAKPOINTS
  ════════════════════════════════ */

  /* ── Tablet: 601px – 900px ── */
  @media (max-width: 900px) and (min-width: 601px) {
    .emi-card {
      flex-direction: column;
    }

    .emi-tabs {
      width: 100%;
      flex-direction: row;
      height: 52px;
      border-right: none;
      border-bottom: 1px solid #d4d4d4;
      justify-content: flex-start;
      padding-left: 16px;
    }

    .emi-tab {
      width: auto;
      padding: 0 20px;
      height: 100%;
    }

    .emi-sliders {
      grid-template-columns: 1fr 1fr;
      row-gap: 20px;
      padding: 20px 20px 16px;
    }

    .emi-details {
      grid-template-columns: 1fr 1fr 1fr;
      padding: 14px 20px 18px;
    }

    .dl-val {
      font-size: 1.15rem;
    }

    .emi-price-col {
      width: 100%;
      border-left: none;
      border-top: 1px solid #d4d4d4;
      flex-direction: row;
      justify-content: center;
      gap: 16px;
      padding: 14px 20px;
    }

    .price-divider {
      width: 2px;
      height: 32px;
    }
  }

  /* ── Mobile: ≤ 600px ── */
  @media (max-width: 600px) {
    .emi-section {
      padding: 28px 12px 40px;
    }

    .emi-heading {
      margin-bottom: 18px;
    }

    .emi-card {
      flex-direction: column;
    }

    /* Top tab bar on mobile */
    .emi-tabs {
      width: 100%;
      flex-direction: row;
      height: 48px;
      border-right: none;
      border-bottom: 1px solid rgba(255,255,255,0.2);
      justify-content: flex-start;
      padding-left: 14px;
      gap: 6px;
    }

    .emi-tab {
      width: auto;
      padding: 0 14px;
      height: 100%;
      gap: 8px;
    }

    /* Stacked sliders on mobile */
    .emi-sliders {
      grid-template-columns: 1fr;
      row-gap: 22px;
      padding: 18px 16px 16px;
      border-bottom: 1px solid #e8e8e8;
    }

    /* Slider value larger touch targets */
    .rs-track {
      height: 28px;
    }

    .rs-thumb {
      width: 24px;
      height: 24px;
      right: -12px;
    }

    /* 2-column details on mobile */
    .emi-details {
      grid-template-columns: 1fr 1fr;
      row-gap: 16px;
      padding: 16px 16px 18px;
    }

    /* EMI amount spans full width */
    .emi-details > div:last-child {
      grid-column: 1 / -1;
      border-top: 1px solid #f0f0f0;
      padding-top: 14px;
    }

    .dl-val {
      font-size: 1.25rem;
    }

    /* Car price row at bottom */
    .emi-price-col {
      width: 100%;
      border-left: none;
      border-top: 1px solid #d4d4d4;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      gap: 14px;
      padding: 14px 16px;
    }

    .price-divider {
      width: 2px;
      height: 28px;
    }

    .price-val {
      font-size: 1.3rem;
    }

    .price-label {
      font-size: 0.6rem;
    }

    .sl-label {
      font-size: 0.6rem;
    }

    .sl-val {
      font-size: 1rem;
    }
  }

  /* ── Very small mobile: ≤ 360px ── */
  @media (max-width: 360px) {
    .emi-details {
      grid-template-columns: 1fr;
    }

    .emi-details > div:last-child {
      grid-column: 1;
      border-top: 1px solid #f0f0f0;
      padding-top: 14px;
    }

    .price-val {
      font-size: 1.1rem;
    }
  }
`;

export default function EMICalculator() {
  const [loanAmount, setLoanAmount] = useState(10000);
  const [interest, setInterest] = useState(10);
  const [tenure, setTenure] = useState(5);

  const emi = calcEMI(loanAmount, interest, tenure);

  return (
    <>
      <style>{css}</style>
      <section className="emi-section" id="emi-calculator">
        <div className="emi-wrap">
          <h2 className="emi-heading">EMI Calculator</h2>

          <div className="emi-card">

            {/* Tab strip */}
            <div className="emi-tabs">
              <div className="emi-tab">
                <EmiIcon />
              </div>
            </div>

            {/* Center */}
            <div className="emi-center">

              {/* Sliders */}
              <div className="emi-sliders">
                <div>
                  <span className="sl-label">LOAN AMOUNT (₹)</span>
                  <span className="sl-val">{fmtINR(loanAmount)}</span>
                  <RangeSlider
                    value={loanAmount} min={10000} max={1898000} step={1000}
                    onChange={setLoanAmount}
                  />
                  <div className="sl-hints">
                    <span>₹10K</span>
                    <span>₹18.98L</span>
                  </div>
                </div>

                <div>
                  <span className="sl-label">INTEREST RATE (%) P.A</span>
                  <span className="sl-val">{interest}%</span>
                  <RangeSlider
                    value={interest} min={7} max={15} step={0.5}
                    onChange={setInterest}
                  />
                  <div className="sl-hints">
                    <span>7%</span>
                    <span>15%</span>
                  </div>
                </div>

                <div>
                  <span className="sl-label">TENURE (YEARS)</span>
                  <span className="sl-val">{tenure} {tenure === 1 ? "Year" : "Years"}</span>
                  <RangeSlider
                    value={tenure} min={1} max={8} step={1}
                    onChange={setTenure}
                  />
                  <div className="sl-hints">
                    <span>1 yr</span>
                    <span>8 yrs</span>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="emi-details">
                <div>
                  <span className="dl-label">LOAN AMOUNT</span>
                  <div className="dl-val">{fmtINR(loanAmount)}</div>
                </div>
                <div>
                  <span className="dl-label">DOWN PAYMENT</span>
                  <div className="dl-val">₹50,000</div>
                </div>
                <div>
                  <span className="dl-label">EMI AMOUNT</span>
                  <div className="dl-val">{fmtINR(emi)}</div>
                </div>
              </div>

            </div>

            {/* Car price */}
            <div className="emi-price-col">
              <div className="price-label">CAR PRICE</div>
              <div className="price-divider" />
              <div className="price-val">₹18,98,000*</div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}